import { DynamicTable } from "@/components/DynamicTable";
import { WithAuth } from "@/hoc/WithAuth";
import { FillLockIcon, UnlockIcon } from "@/styles/icons";
import { formatCardType, formatCurrency, formatCurrencyToApi, formatFinalNumbers, showToast } from "@/utils";
import { Flex, Box, Text, Badge, Skeleton } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { InputCurrencyBRL } from "@/components";
import { useCreditCard } from "@/hooks/useCreditCard";
import { PageHeading } from "barte-design-system";
import { useNavigate } from "react-router-dom";

const ManageCreditCardLimitsPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [snapshotLimits, setSnapshotLimits] = useState([]);
  const navigate = useNavigate();
  const {
    initialDataIsLoading,
    creditCards,
    getInitialData,
    creditCardLimit,
    changeCreditCardLimit,
    editedLimits,
    setEditedLimits,
  } = useCreditCard();

  useEffect(() => {
    if (creditCards.length === 0) {
      getInitialData();
    }
  }, [creditCards]);

  function formatCardStatusIcon(status: string) {
    const cardStatusIcon = {
      ACTIVE: <UnlockIcon size={14} />,
      BLOCKED: <FillLockIcon size={14} />,
    };
    return cardStatusIcon[status] || <FillLockIcon size={14} />;
  }

  function handleStartEditing() {
    setSnapshotLimits([...editedLimits]);
    setIsEditing(true);
  }

  function handleCancelEditing() {
    setEditedLimits(snapshotLimits);
    setIsEditing(false);
  }

  function getChangedCards() {
    return creditCards.reduce((acc, card, idx) => {
      if (card.type !== "FREE_BALANCE" && editedLimits[idx] !== snapshotLimits[idx]) {
        acc.push({ cardId: card.cardId, newLimit: editedLimits[idx] });
      }
      return acc;
    }, []);
  }

  function revertCardLimit(cardId: string) {
    const cardIndex = creditCards.findIndex(c => c.cardId === cardId);
    if (cardIndex !== -1) {
      setEditedLimits(prev =>
        prev.map((limit, idx) => (idx === cardIndex ? snapshotLimits[cardIndex] : limit))
      );
    }
  }

  async function handleSave() {
    const changedCards = getChangedCards();

    if (changedCards.length > 0) {
      const results = await Promise.allSettled(
        changedCards.map(card => changeCreditCardLimit(card))
      );

      const failures = results
        .map((result, i) => ({ result, card: changedCards[i] }))
        .filter(({ result }) => result.status === "rejected");

      if (failures.length === 0) {
        showToast({ type: "success", message: "Limites atualizados com sucesso." });
      } else {
        failures.forEach(({ result, card }) => {
          const error = (result as PromiseRejectedResult).reason;
          error?.response?.data?.errors?.forEach((err) => {
            showToast({ type: "error", message: `${err?.code} - ${err?.description}` });
          });
          revertCardLimit(card.cardId);
        });
      }

      await getInitialData();
    }

    setIsEditing(false);
  }

  function handleLimitChange(idx: number, value: string) {
    setEditedLimits(limits =>
      limits.map((lim: number, i: number) => (i === idx ? formatCurrencyToApi(value) : lim))
    );
  }

  const columns = [
    {
      key: "panMasked",
      title: "Cartão",
      dataIndex: "panMasked",
      sorter: false,
      render: (panMasked) => (
        <Flex align="center" gridGap="2">
          <Text>{`final `}</Text>
          <Text fontWeight={600}>
            {formatFinalNumbers(panMasked)}
          </Text>
        </Flex>
      ),
    },
    {
      key: "cardName",
      title: "Nome",
      dataIndex: "cardName",
      sorter: false,
      render: (cardName, row) => (
        <Flex align="center" gridGap="2">
          {formatCardStatusIcon(row.cardStatus)}
          <Text>{cardName}</Text>
        </Flex>
      ),
    },
    {
      key: "",
      title: "",
      dataIndex: "",
      sorter: false,
      emptyText: true,
    },
    {
      key: "type",
      title: "",
      dataIndex: "type",
      sorter: false,
      emptyText: true,
      render: (type: string) => (
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          fontWeight={400}
          fontSize="sm"
          color="gray.700"
          bg="gray.25"
        >
          {formatCardType(type)}
        </Badge>
      ),
    },
    {
      key: "invoiceValue",
      title: "Fatura atual",
      dataIndex: "invoiceValue",
      sorter: false,
      render: (invoiceValue) => (
        <Text
          fontWeight={400}
          fontSize="12px"
        >
          {formatCurrency({ value: invoiceValue })}
        </Text>
      ),
    },
    {
      key: "limite",
      title: "Limite",
      dataIndex: "limite",
      sorter: false,
      render: (limitValue, row, rowIndex) => {
        const isFreeBalance = row?.type === "FREE_BALANCE";
        return (
          <InputCurrencyBRL
            value={editedLimits[rowIndex]}
            onChangeInput={e => handleLimitChange(rowIndex, e)}
            fontSize="12px"
            borderRadius="8px"
            isLimit
            _focus={{
              borderColor: "black",
            }}
            isDisabled={!isEditing || isFreeBalance}
          />
        );
      },
    },
  ];

  return (
    <Flex flexDir="column" gap={4}>
      <PageHeading
        title="Gerenciar limites"
        onBack={() => navigate(-1)}
        onPrimaryAction={isEditing ? handleSave : handleStartEditing}
        primaryActionLabel={isEditing ? "Salvar" : "Editar"}
        onSecondaryAction={handleCancelEditing}
        secondaryActionLabel={isEditing ? "Cancelar" : null}
      />

      <Flex align="flex-end" justify="space-between" mb={6}>
        <Box textAlign="right">
          <Text color="#A3A3A3" fontSize="sm">Limite global</Text>
          <Skeleton isLoaded={!initialDataIsLoading}>
            <Text fontWeight={700} fontSize="2xl">{formatCurrency({ value: creditCardLimit?.totalValueBag })}</Text>
          </Skeleton>
          <Text color="#A3A3A3" fontSize="sm">Limite restante
            <Skeleton isLoaded={!initialDataIsLoading}><b>{formatCurrency({ value: creditCardLimit?.availableValueVirtualCard })}</b></Skeleton>
          </Text>
        </Box>
      </Flex>
      <DynamicTable
        data={creditCards}
        columns={columns}
        loading={initialDataIsLoading}
      />
    </Flex>
  );
}

export default WithAuth(ManageCreditCardLimitsPage);
